import {
	faExclamationTriangle,
	faSpinner
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Editor from "@monaco-editor/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { ApiError, handleApiError } from "@/utils/errorHandler";

interface FlowDefinition {
	id: string;
	flowActionId: string;
	flowTargetId: string;
	description: string;
	code: string;
	createdAt?: string;
	updatedAt?: string;
}

interface AddFlowDefinitionProps {
	flowTargetId: string;
	flowActionId: string;
	definition?: FlowDefinition | null;
	onClose: () => void;
}

const FLOW_DEFINITIONS_QUERY_KEY = ["flow-definition"] as const;

const AddFlowDefinition: React.FC<AddFlowDefinitionProps> = ({
	flowTargetId,
	flowActionId,
	definition,
	onClose
}) => {
	const queryClient = useQueryClient();
	const [formData, setFormData] = useState({
		description: definition?.description || "",
		code: definition?.code || ""
	});

	const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
	const createFlowDefinitionMutation = useMutation({
		mutationFn: async (
			data: Omit<FlowDefinition, "id" | "createdAt" | "updatedAt">
		) => {
			const { data: response } = await apiClient.post(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_DEFINITIONS", "CREATE"]),
				data
			);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: FLOW_DEFINITIONS_QUERY_KEY });
			toast.success("Flow definition created successfully");
			onClose();
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to create flow definition");
		}
	});

	const updateFlowDefinitionMutation = useMutation({
		mutationFn: async ({
			id,
			data
		}: {
			id: string;
			data: Omit<FlowDefinition, "id" | "createdAt" | "updatedAt">;
		}) => {
			const { data: response } = await apiClient.put(
				resolveEndpoint(
					API_ENDPOINTS,
					["FLOW_DEFINITIONS", "UPDATE_BY_ID"],
					id
				),
				data
			);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: FLOW_DEFINITIONS_QUERY_KEY });
			toast.success("Flow definition updated successfully");
			onClose();
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to update flow definition");
		}
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (!formData.code.trim()) {
			toast.error("Code is required");
			return;
		}

		const payload = {
			flowActionId,
			flowTargetId,
			description: formData.description.trim(),
			code: formData.code
		};

		if (definition) {
			updateFlowDefinitionMutation.mutate({
				id: definition.id,
				data: payload
			});
		} else {
			createFlowDefinitionMutation.mutate(payload);
		}
	};

	const handleCancel = () => {
		setIsDiscardModalOpen(true);
	};

	const isLoading =
		createFlowDefinitionMutation.isPending ||
		updateFlowDefinitionMutation.isPending;

	return (
		<div className="space-y-4">
			<form onSubmit={handleSubmit}>
				<div className="flex flex-col sm:flex-row items-start sm:items-center mb-4 sm:space-x-4 space-y-2 sm:space-y-0">
					<label
						htmlFor="description"
						className="text-sm font-medium text-gray-700 whitespace-nowrap"
					>
						Description:
					</label>
					<input
						type="text"
						value={formData.description}
						onChange={(e) =>
							setFormData((prev) => ({ ...prev, description: e.target.value }))
						}
						className="w-full flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
						placeholder="Enter description"
						disabled={isLoading}
					/>
				</div>

				<div className="border rounded-md bg-white mb-4">
					<Editor
						className="border border-gray-300 rounded-md shadow"
						height="500px"
						defaultLanguage="typescript"
						value={formData.code}
						onChange={(value) =>
							setFormData((prev) => ({ ...prev, code: value || "" }))
						}
						options={{
							minimap: { enabled: false },
							fontSize: 14,
							padding: { top: 10 },
							lineNumbers: "on",
							scrollBeyondLastLine: false,
							wordWrap: "on"
						}}
						onMount={(editor, monacoInstance) => {
							editor.focus();
							const model = editor.getModel();
							if (model) {
								monacoInstance.editor.setModelLanguage(model, "typescript");

								const formatAction = editor.getAction(
									"editor.action.formatDocument"
								);
								if (formatAction) {
									formatAction.run();
								}
							}
						}}
					/>
				</div>

				<div className="flex items-center justify-end space-x-2">
					<button
						type="submit"
						disabled={isLoading || !formData.code.trim()}
						className="px-3 py-2 bg-blue-fynxt text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{isLoading ? (
							<>
								<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
								{definition ? "Updating..." : "Creating..."}
							</>
						) : definition ? (
							"Update"
						) : (
							"Create"
						)}
					</button>
					<button
						type="button"
						onClick={handleCancel}
						disabled={isLoading}
						className="px-3 py-2 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 disabled:opacity-50"
					>
						Cancel
					</button>
				</div>
			</form>
			{isDiscardModalOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div
							className="fixed inset-0 transition-opacity"
							aria-hidden="true"
						>
							<div className="absolute inset-0 bg-gray-500 opacity-75" />
						</div>

						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
										<FontAwesomeIcon
											icon={faExclamationTriangle}
											className="h-6 w-6 text-red-600"
										/>
									</div>
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
										<h3 className="text-lg leading-6 font-medium text-gray-900">
											Discard Changes
										</h3>
										<div className="mt-2">
											<p className="text-sm text-gray-500">
												Are you sure you want to discard your changes?
											</p>
										</div>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={() => onClose()}
								>
									Discard
								</button>
								<button
									type="button"
									onClick={() => setIsDiscardModalOpen(false)}
									className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AddFlowDefinition;
